import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { API_SERVER } from '../config/env';
import { getAuthToken } from '../services/authSession';

const SOCKET_URL = typeof API_SERVER === 'string' ? API_SERVER.trim().replace(/\/+$/, '') : '';

/**
 * Entra numa sala do socket e chama `onEvent` a cada `event` recebido.
 *
 * @param {string|null} room
 * @param {string} event
 * @param {(payload: object) => void} onEvent
 * @param {{ enabled?: boolean }} [options]
 */
export function useClubRoomSocket(room, event, onEvent, { enabled = true } = {}) {
    const [connected, setConnected] = useState(false);
    const onEventRef = useRef(onEvent);

    useEffect(() => {
        onEventRef.current = onEvent;
    }, [onEvent]);

    useEffect(() => {
        const token = getAuthToken();
        if (!enabled || !SOCKET_URL || !room || !token) {
            setConnected(false);
            return undefined;
        }

        const socket = io(SOCKET_URL, {
            auth: { token },
            transports: ['websocket'],
            reconnection: true,
            reconnectionAttempts: 12,
            reconnectionDelay: 1500,
        });

        const onConnect = () => {
            setConnected(true);
            socket.emit('join:room', room);
        };
        const onDisconnect = () => setConnected(false);
        const handler = payload => onEventRef.current?.(payload);

        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);
        socket.on(event, handler);
        if (socket.connected) onConnect();

        return () => {
            socket.emit('leave:room', room);
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
            socket.off(event, handler);
            socket.disconnect();
            setConnected(false);
        };
    }, [room, event, enabled]);

    return { connected };
}

export default useClubRoomSocket;
